import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, type PanInfo } from 'framer-motion';
import {
  Coins, Undo2, X, Check, SkipForward, Sparkles, Loader2, ArrowLeft, ArrowRight,
  Tv, Vote, Smartphone, Signal, Briefcase, HelpCircle, type LucideIcon,
} from 'lucide-react';
import NavShell from '../components/NavShell';
import {
  fetchSwipeDeck,
  submitSwipeAnswer,
  fetchQualifyingCount,
  type SwipeCard,
  type SwipeDirection,
  type SwipeAnswerResult,
} from '../lib/api';

// The deck's answerValueMap carries canonical VALUES (yes/no/iphone/…); the deck
// endpoint does not return display labels, so we prettify here. If richer/localized
// option labels are ever needed, the /deck response should carry them (flagged).
const VALUE_LABELS: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  iphone: 'iPhone',
  android: 'Android',
  pre_paid: 'Pre-paid',
  contract: 'Contract',
};
function optLabel(v: string): string {
  return VALUE_LABELS[v] ?? v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Generic, category-appropriate iconography per card key. NEVER a real brand mark
// (Netflix/Disney/etc. logos are trademarked) — a neutral streaming glyph stands in
// for all streaming services. Unknown keys fall back to a question glyph.
const STREAMING_KEYS = new Set([
  'netflix', 'hulu', 'disney_plus', 'max', 'prime_video', 'peacock', 'paramount_plus', 'apple_tv_plus',
]);
function cardIcon(key: string): LucideIcon {
  if (STREAMING_KEYS.has(key)) return Tv;
  switch (key) {
    case 'voter_registration': return Vote;
    case 'smartphone_type': return Smartphone;
    case 'cell_plan': return Signal;
    case 'decision_maker': return Briefcase;
    default: return HelpCircle;
  }
}

// Category pill label — derived from the REAL v1 card set, never invented. Most
// cards are the 8 streaming booleans ("Streaming"); the rest map to their domain.
function cardCategory(key: string): string {
  if (STREAMING_KEYS.has(key)) return 'Streaming';
  switch (key) {
    case 'voter_registration': return 'Civic';
    case 'smartphone_type': return 'Tech';
    case 'cell_plan': return 'Tech';
    case 'decision_maker': return 'Work';
    default: return 'About you';
  }
}

const SWIPE_THRESHOLD = 110; // px drag distance to commit a left/right swipe

type AnsweredCard = { card: SwipeCard; direction: SwipeDirection; result: SwipeAnswerResult };

export default function SwipePage() {
  const [cards, setCards] = useState<SwipeCard[] | null>(null);
  const [allSet, setAllSet] = useState(false);
  const [index, setIndex] = useState(0);
  const [history, setHistory] = useState<AnsweredCard[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [exitDir, setExitDir] = useState<SwipeDirection>('right');
  // First-time coach hint. Session-scoped only (shows once per page load) — no
  // browser storage is available in this env. Truly-once-ever would need a
  // backend "has swiped before" flag (member swipe_response history); not adding
  // one now — session-scoped is the accepted v1.
  const [coachDismissed, setCoachDismissed] = useState(false);
  const busyRef = useRef(false); // guards double-submit from fast keys/drags

  useEffect(() => {
    let alive = true;
    fetchSwipeDeck()
      .then((deck) => {
        if (!alive) return;
        setCards(deck.cards);
        setAllSet(deck.allSet);
      })
      .catch((e: Error) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, []);

  const current = cards ? cards[index] : undefined;
  const finished = cards != null && index >= cards.length;

  const answer = useCallback(
    async (direction: SwipeDirection) => {
      if (busyRef.current || !current) return;
      busyRef.current = true;
      setCoachDismissed(true);
      setExitDir(direction);
      setError(null);
      // Optimistic advance — the card animates out immediately; the POST resolves
      // in the background. Re-answering (via Undo) is idempotent server-side
      // (attribute upsert + ledger dedup on cardKey), so an in-flight race is safe.
      const card = current;
      setIndex((i) => i + 1);
      try {
        const result = await submitSwipeAnswer(card.key, direction);
        setHistory((h) => [...h, { card, direction, result }]);
      } catch (e) {
        setError((e as Error).message);
        setIndex((i) => Math.max(0, i - 1)); // roll the card back on failure
      } finally {
        busyRef.current = false;
      }
    },
    [current],
  );

  // Undo: re-open the last card for a fresh answer. The previous answer already
  // POSTed (attribute written, participation credited ONCE); re-answering upserts
  // the new value and the ledger dedup prevents a second credit — so Undo means
  // "change my answer", the point is already banked.
  const undo = useCallback(() => {
    if (busyRef.current) return;
    setHistory((h) => {
      if (h.length === 0) return h;
      setIndex((i) => Math.max(0, i - 1));
      setError(null);
      return h.slice(0, -1);
    });
  }, []);

  // Desktop keyboard: ←/→ = the two answers, ↓/space = skip, U/backspace = undo.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (finished || !current) {
        if (e.key.toLowerCase() === 'u' || e.key === 'Backspace') {
          e.preventDefault();
          undo();
        }
        return;
      }
      switch (e.key) {
        case 'ArrowLeft': e.preventDefault(); void answer('left'); break;
        case 'ArrowRight': e.preventDefault(); void answer('right'); break;
        case 'ArrowDown': case ' ': e.preventDefault(); void answer('skip'); break;
        case 'u': case 'U': case 'Backspace': e.preventDefault(); undo(); break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [answer, undo, finished, current]);

  const onDragEnd = (_e: unknown, info: PanInfo) => {
    if (info.offset.x > SWIPE_THRESHOLD) void answer('right');
    else if (info.offset.x < -SWIPE_THRESHOLD) void answer('left');
  };

  return (
    <NavShell>
      <div className="max-w-lg mx-auto px-4 py-6 sm:py-8">
        <header className="mb-6 sm:mb-8 text-center">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary/80 mb-2">
            <Sparkles className="w-3.5 h-3.5" /> Quick questions
          </span>
          <h1 className="text-xl sm:text-2xl font-bold font-display text-navy leading-tight text-balance">
            Unlock better surveys
          </h1>
          <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto leading-relaxed">
            {cards && cards.length > 0 && !finished
              ? `${cards.length - index} question${cards.length - index === 1 ? '' : 's'} remaining — each one matches you with better-paying, more relevant surveys.`
              : 'A few quick answers match you with better-paying, more relevant surveys.'}
          </p>
        </header>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3" role="alert">
            {error}
          </div>
        )}

        {cards == null && !error && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin mb-3" />
            Finding your cards…
          </div>
        )}

        {cards != null && (allSet || cards.length === 0) && (
          <AllSetState />
        )}

        {cards != null && cards.length > 0 && (
          finished ? (
            <DeckSummary history={history} onRestart={undo} />
          ) : (
            <>
              <div className="relative h-[25rem] mb-6 select-none [perspective:1200px]">
                {/* Peek stack — the next card(s) rise into place as the top card
                    leaves (keyed per card so framer animates the transition).
                    Depth reflects the real remaining cards. */}
                {[2, 1].map((depth) => {
                  const peek = cards[index + depth];
                  if (!peek) return null;
                  return (
                    <motion.div
                      key={peek.key}
                      aria-hidden="true"
                      className="absolute inset-0 rounded-[1.75rem] bg-white shadow-[0_10px_30px_-12px_rgba(2,6,23,0.18)]"
                      initial={false}
                      animate={{ scale: depth === 1 ? 0.955 : 0.91, y: depth === 1 ? 14 : 28 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 32 }}
                    />
                  );
                })}
                <AnimatePresence initial={false}>
                  {current && (
                    <SwipeCardView
                      key={current.key}
                      card={current}
                      index={index}
                      total={cards.length}
                      exitDir={exitDir}
                      onDragEnd={onDragEnd}
                      onDragStart={() => setCoachDismissed(true)}
                    />
                  )}
                </AnimatePresence>

                {/* First-time coach hint — session-scoped, dismisses on first move. */}
                <AnimatePresence>
                  {!coachDismissed && index === 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ delay: 0.5 }}
                      className="pointer-events-none absolute -bottom-3 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap rounded-full bg-navy/90 px-4 py-2 text-xs font-medium text-white shadow-lg"
                    >
                      Swipe right for Yes, left for No — or tap the buttons below
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Controls card={current!} onAnswer={answer} onSkip={() => answer('skip')} onUndo={undo} canUndo={history.length > 0} />

              <p className="mt-5 text-center text-[11px] text-gray-400 hidden sm:block">
                ← / → to choose · ↓ or space to skip · U to undo · or drag the card
              </p>
            </>
          )
        )}
      </div>
    </NavShell>
  );
}

function SwipeCardView({
  card,
  index,
  total,
  exitDir,
  onDragEnd,
  onDragStart,
}: {
  card: SwipeCard;
  index: number;
  total: number;
  exitDir: SwipeDirection;
  onDragEnd: (e: unknown, info: PanInfo) => void;
  onDragStart: () => void;
}) {
  const leftLabel = optLabel(card.answerValueMap.left);
  const rightLabel = optLabel(card.answerValueMap.right);
  const Icon = cardIcon(card.key);
  const binary = card.answerModel === 'binary';

  // Drag-driven physics: `x` is the live offset; rotation, the two overlay
  // opacities, and the growing directional arrows all derive from it. Exit
  // animates the same `x`, so the card keeps tilting as it flies off.
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-240, 0, 240], [-15, 0, 15]);
  const leftHint = useTransform(x, [-140, -25, 0], [1, 0, 0]);
  const rightHint = useTransform(x, [0, 25, 140], [0, 0, 1]);
  const leftArrow = useTransform(x, [-140, 0], [1.7, 1]);
  const rightArrow = useTransform(x, [0, 140], [1, 1.7]);

  // Overlay colour is model-aware: a binary "No" is a real negative (warm red);
  // a this_or_that left option is just the OTHER choice (neutral navy), never
  // styled as rejection. Right is primary for both.
  const leftTint = binary ? 'bg-rose-500/10 ring-rose-400/70' : 'bg-navy/[0.06] ring-navy/25';
  const leftBadge = binary ? 'bg-rose-500 text-white shadow-rose-500/30' : 'bg-navy text-white shadow-navy/20';

  const exit =
    exitDir === 'skip'
      ? { y: 420, opacity: 0, scale: 0.88 }
      : { x: exitDir === 'left' ? -640 : 640, opacity: 0 };

  return (
    <motion.div
      style={{ x, rotate }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.65}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      initial={{ scale: 0.92, opacity: 0, y: 26 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={exit}
      transition={{ type: 'spring', stiffness: 300, damping: 26 }}
      whileTap={{ scale: 0.985, cursor: 'grabbing' }}
      className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing overflow-hidden rounded-[1.75rem] bg-white shadow-[0_28px_60px_-18px_rgba(15,23,42,0.32)] flex flex-col p-6 sm:p-7"
    >
      {/* Top: category pill + progress. */}
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-light px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-primary">
          <Icon className="w-3.5 h-3.5" strokeWidth={2.25} /> {cardCategory(card.key)}
        </span>
        <span className="text-xs font-semibold text-gray-400 tabular-nums">
          {index + 1} <span className="text-gray-300">/ {total}</span>
        </span>
      </div>

      {/* Center: large icon + the question. */}
      <div className="flex-1 flex flex-col items-center justify-center text-center gap-6 px-1">
        <div className="w-[4.5rem] h-[4.5rem] rounded-3xl bg-gradient-to-br from-primary-light to-primary/10 flex items-center justify-center ring-1 ring-primary/10">
          <Icon className="w-9 h-9 text-primary" strokeWidth={1.75} />
        </div>
        <h2 className="text-[1.65rem] sm:text-[1.8rem] font-bold font-display text-navy leading-[1.2] text-balance">
          {card.prompt}
        </h2>
      </div>

      {/* Bottom: directional hints whose arrows grow as the card moves. */}
      <div className="flex items-center justify-between text-xs font-semibold text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <motion.span style={{ scale: leftArrow }} className="inline-flex origin-center">
            <ArrowLeft className="w-4 h-4" />
          </motion.span>
          {leftLabel}
        </span>
        <span className="inline-flex items-center gap-1.5">
          {rightLabel}
          <motion.span style={{ scale: rightArrow }} className="inline-flex origin-center">
            <ArrowRight className="w-4 h-4" />
          </motion.span>
        </span>
      </div>

      {/* Drag colour overlays — fade in with direction, showing the outcome
          before release. Non-interactive. */}
      <motion.div
        style={{ opacity: leftHint }}
        className={`absolute inset-0 rounded-[1.75rem] ring-2 flex items-start justify-start p-6 pointer-events-none ${leftTint}`}
      >
        <span className={`inline-flex items-center gap-1.5 rounded-2xl text-base font-extrabold px-4 py-1.5 -rotate-[8deg] shadow-lg ${leftBadge}`}>
          {binary ? <X className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />} {leftLabel}
        </span>
      </motion.div>
      <motion.div
        style={{ opacity: rightHint }}
        className="absolute inset-0 rounded-[1.75rem] ring-2 ring-primary bg-primary/[0.07] flex items-start justify-end p-6 pointer-events-none"
      >
        <span className="inline-flex items-center gap-1.5 rounded-2xl bg-primary text-white text-base font-extrabold px-4 py-1.5 rotate-[8deg] shadow-lg shadow-primary/30">
          {binary ? <Check className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />} {rightLabel}
        </span>
      </motion.div>
    </motion.div>
  );
}

function Controls({
  card,
  onAnswer,
  onSkip,
  onUndo,
  canUndo,
}: {
  card: SwipeCard;
  onAnswer: (d: SwipeDirection) => void;
  onSkip: () => void;
  onUndo: () => void;
  canUndo: boolean;
}) {
  const leftLabel = optLabel(card.answerValueMap.left);
  const rightLabel = optLabel(card.answerValueMap.right);
  return (
    <div className="flex items-center justify-center gap-2.5">
      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        aria-label="Undo"
        className="shrink-0 p-3 rounded-full bg-white ring-1 ring-black/[0.06] text-gray-400 shadow-sm hover:text-navy hover:ring-black/10 disabled:opacity-30 disabled:hover:text-gray-400 transition active:scale-95"
      >
        <Undo2 className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={() => onAnswer('left')}
        className="flex-1 max-w-[8.5rem] py-3.5 rounded-2xl bg-white ring-1 ring-black/10 font-semibold text-gray-700 shadow-sm hover:ring-primary/40 hover:text-navy transition active:scale-[0.97]"
      >
        {leftLabel}
      </button>
      <button
        type="button"
        onClick={onSkip}
        className="shrink-0 inline-flex items-center gap-1 px-3 py-3 rounded-full text-xs font-semibold text-gray-400 hover:text-navy transition active:scale-95"
      >
        <SkipForward className="w-4 h-4" /> Skip
      </button>
      <button
        type="button"
        onClick={() => onAnswer('right')}
        className="flex-1 max-w-[8.5rem] py-3.5 rounded-2xl bg-primary text-white font-semibold shadow-md shadow-primary/30 hover:bg-primary-dark transition active:scale-[0.97]"
      >
        {rightLabel}
      </button>
    </div>
  );
}

function AllSetState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-full bg-primary-light flex items-center justify-center mb-4">
        <Check className="w-8 h-8 text-primary" />
      </div>
      <h2 className="text-xl font-bold font-display text-navy">You're all set</h2>
      <p className="text-sm text-gray-500 mt-2 max-w-xs">
        You've answered every card for now. Check back soon — new cards appear as
        surveys need them.
      </p>
    </div>
  );
}

function DeckSummary({ history, onRestart }: { history: AnsweredCard[]; onRestart: () => void }) {
  const points = history.reduce((s, h) => s + (h.result.credited ?? 0), 0);
  const answered = history.filter((h) => h.result.recorded === 'answered').length;

  // The honest reward signal: how many surveys the member CURRENTLY qualifies
  // for, fetched once at deck completion. Present-moment snapshot (inventory
  // churns) → framed present-tense, never "unlocked" (a durable claim). On error
  // we fall back to a neutral profile-updated message rather than a fake number.
  const [count, setCount] = useState<number | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  useEffect(() => {
    let alive = true;
    fetchQualifyingCount()
      .then(({ count }) => {
        if (!alive) return;
        setCount(count);
        setState('ok');
      })
      .catch(() => alive && setState('error'));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-16 h-16 rounded-full bg-primary-light flex items-center justify-center mb-4"
      >
        <Sparkles className="w-8 h-8 text-primary" />
      </motion.div>

      {state === 'loading' && (
        <>
          <h2 className="text-2xl font-bold font-display text-navy">Nice work!</h2>
          <p className="text-sm text-gray-500 mt-2 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your surveys…
          </p>
        </>
      )}

      {state === 'ok' && (count ?? 0) > 0 && (
        <>
          <h2 className="text-2xl font-bold font-display text-navy">
            You currently qualify for {count} {count === 1 ? 'survey' : 'surveys'}
          </h2>
          <p className="text-sm text-gray-500 mt-2">
            Head to your surveys to start earning — right now.
          </p>
        </>
      )}

      {state === 'ok' && count === 0 && (
        <>
          <h2 className="text-2xl font-bold font-display text-navy">Keep building your profile</h2>
          <p className="text-sm text-gray-500 mt-2 max-w-xs">
            No matches this moment — more unlock as surveys refresh, and every answer
            helps us match you.
          </p>
        </>
      )}

      {state === 'error' && (
        <>
          <h2 className="text-2xl font-bold font-display text-navy">Profile updated</h2>
          <p className="text-sm text-gray-500 mt-2">
            {answered} {answered === 1 ? 'card' : 'cards'} answered — thanks!
          </p>
        </>
      )}

      {points > 0 && (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-light px-4 py-2 text-primary font-semibold">
          <Coins className="w-4 h-4" /> +{points} points
        </div>
      )}
      {history.length > 0 && (
        <button
          type="button"
          onClick={onRestart}
          className="mt-8 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-navy transition"
        >
          <Undo2 className="w-4 h-4" /> Undo last card
        </button>
      )}
    </div>
  );
}
